import { FEATURE_KIND, FEATURE_PERIMETER } from '../../model/enums';
import { Model } from '../../model/model';
import { ViewData } from '../../model/view.data';
import { FeatureVO } from '../../vo/core/feature.vo';

/**
 * Engine dédié aux features
 */
export class FeatureEngine {

  protected _model: Model = null;

  constructor(model: Model) {
    this._model = model;
  }
  public get model(): Model {
    return this._model as Model;
  }
  
  public feedMap(features: FeatureVO[]): void {
    if (!this.model.featureMap) {
      this.model.featureMap = new Map<string, Map<string, FeatureVO>>();
    }

    features.forEach((feat: FeatureVO) => {
      if (!this.model.featureMap.has(feat.businessUnitCode)) {
        // ventilation dans la map par BU. Toutes les features techniques seront dans *
        this.model.featureMap.set(feat.businessUnitCode, new Map<string, FeatureVO>());
      }
      this.model.featureMap.get(feat.businessUnitCode).set(feat.code, feat);
    });
  }

  /**
   * Retourne la MEME instance de feature que dans la liste du modèle.
   * @param source : FeatureVO cherché
   */
  public getCurrentInstance(source: FeatureVO): FeatureVO {
    return this.model.featureMap.get(source.businessUnitCode).get(source.code);
  }

  /**
   * La feature est elle utilisée ?
   * @param feature La feature
   * @return boolean Le résultat du test
   */
  public isUsedFeature(feature: FeatureVO): boolean {
    const currentfeature: FeatureVO = this.getCurrentInstance(feature);
    let result: boolean = false; // on considère que non

    if(currentfeature && this.model.rolesMap.has(currentfeature.businessUnitCode)) {
      if(this.model.rolesMap.get(currentfeature.businessUnitCode).has(currentfeature.code)) {
        result = true;
      }
    }
    return result;
  }

  /**
   * Création de 3 features parent pour les features techniques,  les features dynamiques et les pools de données
   * Cette liste servira pour le Tree
   * Les features dynamiques sont représentées en un arbre de vision, vision_step, vision_step_perimeter, tab
   * @return FeatureVO[] liste contenant la concatenant des features techniques et dynamiques et 2 features servant de parent pour chaque type
   */
  public getFeatureTreeData(): FeatureVO[] {
    const result: Map<string, FeatureVO> = new Map<string, FeatureVO>(); // collection de retour

    // Création du noeud portant les features techniques
    const tech: FeatureVO = new FeatureVO(this.model);
    tech.visionCode = ViewData.wildcardDomain;
    tech.code = ViewData.TECH_FEATURE;
    tech.parentCode = null;
    tech.headerFeatureLabel = '$.role.view.technical.features.$';
    tech.isFakeLevel = true;
    // result.set(tech.uniqueKey, tech);

    // on boucle sur tous les éléments de la map de map.
    Array.from(this.model.featureMap.keys()).forEach((buCode: string) => {
      const feats: FeatureVO[] = Array.from(this.model.featureMap.get(buCode).values());
      if (buCode === ViewData.wildcardDomain) {
        // les features techniques
        feats
          .filter((feat) => feat.perimeter === FEATURE_PERIMETER.APP)
          .forEach((feat: FeatureVO) => {
            feat.parentCode = tech.uniqueKey; // on affecte TECH à toutes les features techniques
            result.set(feat.uniqueKey, feat);
          });
      } else {
        // les visions et tout leur stuff

        const byVisionMap: Map<string, FeatureVO[]> = new Map<string, FeatureVO[]>();
        // const visionFeatMap: Map<string, FeatureVO> = new Map<string, FeatureVO>();
        feats.forEach((feat: FeatureVO) => {
          if (!byVisionMap.has(feat.visionCode)) {
            byVisionMap.set(feat.visionCode, []);
          }

          byVisionMap.get(feat.visionCode).push(feat);
          if (feat.kind === FEATURE_KIND.VISION) {
            // on alimente la map des visions et on lui ajoute ses 2 enfants directs
            // visionFeatMap.set(feat.visionCode, feat);
            // Création du noeud portant les features dynamiques s'il y en a
            const dyna: FeatureVO = new FeatureVO(this.model);
            dyna.visionCode = feat.visionCode;
            dyna.code = ViewData.DYNA_FEATURE;
            dyna.headerFeatureLabel = '$.role.view.dynamical.features.$';
            dyna.isFakeLevel = true;
            dyna.parentCode = feat.uniqueKey;
            result.set(dyna.uniqueKey, dyna);

            // Création du noeud portant les pools de données
            const data: FeatureVO = new FeatureVO(this.model);
            data.visionCode = feat.visionCode;
            data.code = ViewData.DATA_FEATURE;
            data.headerFeatureLabel = '$.role.view.data.features.$';
            data.isFakeLevel = true;
            data.parentCode = feat.uniqueKey;
            result.set(data.uniqueKey, data);
          }
        });

        // on peut boucler sur chaque Map par vision, les sous-levels de la vision sont présent dans result
        Array.from(byVisionMap.keys()).forEach((visionKey: string) => {
          byVisionMap.get(visionKey).forEach((feat: FeatureVO) => {
            // on boucle par vision
            switch (feat.kind) {
              case FEATURE_KIND.VISION:
                feat.parentCode = null;
                break;
              case FEATURE_KIND.VISION_STEP:
                feat.parentCode = result.get(visionKey + ViewData.KEY_SEPARATOR + ViewData.DATA_FEATURE).uniqueKey;
                break;
              case FEATURE_KIND.TAB:
                feat.parentCode = result.get(visionKey + ViewData.KEY_SEPARATOR + ViewData.DYNA_FEATURE).uniqueKey;
                break;
            }
            result.set(feat.uniqueKey, feat); // ajout à la map de result
          });
        });
      }
    });
    return Array.from(result.values());
  }
}
